import React, { useMemo } from 'react';
import { Button, Dropdown, Label } from 'semantic-ui-react';
import { EContractStatus } from '../../services/contracts/ContractService';
import { getContractStatusText, normalizeContractStatus } from '../../utils/contractStatusUtils';
import {
  HubCurrentStatusFilter,
  hubStatusFilterLabel,
  isHubAtivoOnlyFilter,
} from './clientContractHubUtils';

interface HubStatusFilterBarProps {
  value: HubCurrentStatusFilter;
  onChange: (next: HubCurrentStatusFilter) => void;
  totalContracts: number;
  visibleContracts: number;
}

const QUICK_PRESETS: { key: string; label: string; value: HubCurrentStatusFilter }[] = [
  { key: 'ativo', label: 'Só Ativos', value: [EContractStatus.Ativo] },
  { key: 'todos', label: 'Todos', value: 'all' },
  {
    key: 'cancelado',
    label: 'Cancelados',
    value: [EContractStatus.Cancelado],
  },
  {
    key: 'assinado',
    label: 'Assinados',
    value: [EContractStatus.Assinado],
  },
];

export const HubStatusFilterBar: React.FC<HubStatusFilterBarProps> = ({
  value,
  onChange,
  totalContracts,
  visibleContracts,
}) => {
  const statusOptions = useMemo(
    () =>
      Object.entries(EContractStatus)
        .filter(([, v]) => typeof v === 'number')
        .map(([, v]) => ({
          key: String(v),
          value: v as number,
          text: getContractStatusText(v as EContractStatus),
        })),
    []
  );

  const isAll = value === 'all' || (Array.isArray(value) && value.length === 0);
  const customDropdownValue = value === 'all' ? [] : value;

  const presetActive = (preset: HubCurrentStatusFilter) => {
    if (preset === 'all') return isAll;
    if (value === 'all' || !Array.isArray(value)) return false;
    if (preset.length !== value.length) return false;
    return preset.every((s) => value.includes(s));
  };

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        alignItems: 'center',
        marginBottom: 12,
      }}
    >
      <strong style={{ marginRight: 4 }}>Status atual:</strong>
      {QUICK_PRESETS.map((preset) => (
        <Button
          key={preset.key}
          size="small"
          primary={presetActive(preset.value)}
          basic={!presetActive(preset.value)}
          onClick={() => onChange(preset.value)}
        >
          {preset.label}
        </Button>
      ))}
      <Dropdown
        selection
        multiple
        search
        options={statusOptions}
        value={customDropdownValue}
        onChange={(_, d) => {
          const raw = (d.value as Array<number | string>) ?? [];
          const selected = raw
            .map((s) => normalizeContractStatus(s))
            .filter((s): s is number => s !== null);
          onChange(selected.length === 0 ? 'all' : (selected as EContractStatus[]));
        }}
        placeholder="Escolher status..."
        style={{ minWidth: 220 }}
      />
      <Label color={isHubAtivoOnlyFilter(value) ? 'green' : 'blue'}>
        {hubStatusFilterLabel(value)} · {visibleContracts} de {totalContracts} contrato(s)
      </Label>
    </div>
  );
};

export default HubStatusFilterBar;
